import { z } from 'zod';

export const ERROR_TYPES = [
  'invalid_request_error',
  'authentication_error',
  'permission_error',
  'not_found_error',
  'conflict_error',
  'request_too_large',
  'rate_limit_error',
  'api_error',
  'overloaded_error',
] as const;
export const ErrorTypeSchema = z.enum(ERROR_TYPES);
export type ErrorType = z.infer<typeof ErrorTypeSchema>;

export const ERROR_CODES = {
  invalid_request: { status: 400, type: 'invalid_request_error' },
  mcp_server_not_registered: { status: 400, type: 'invalid_request_error' },
  conversation_token_budget_exceeded: { status: 400, type: 'invalid_request_error' },
  unauthenticated: { status: 401, type: 'authentication_error' },
  invalid_api_key: { status: 401, type: 'authentication_error' },
  session_expired: { status: 401, type: 'authentication_error' },
  session_revoked: { status: 401, type: 'authentication_error' },
  mcp_authorization_expired: { status: 401, type: 'authentication_error' },
  forbidden: { status: 403, type: 'permission_error' },
  origin_not_allowed: { status: 403, type: 'permission_error' },
  tool_not_allowed: { status: 403, type: 'permission_error' },
  not_found: { status: 404, type: 'not_found_error' },
  conflict: { status: 409, type: 'conflict_error' },
  conversation_busy: { status: 409, type: 'conflict_error' },
  file_expired: { status: 410, type: 'not_found_error' },
  payload_too_large: { status: 413, type: 'request_too_large' },
  rate_limited: { status: 429, type: 'rate_limit_error' },
  credit_limit_reached: { status: 429, type: 'rate_limit_error' },
  internal: { status: 500, type: 'api_error' },
  not_implemented: { status: 501, type: 'api_error' },
  mcp_unavailable: { status: 502, type: 'api_error' },
  model_unavailable: { status: 503, type: 'api_error' },
  sandbox_unavailable: { status: 503, type: 'api_error' },
  storage_unavailable: { status: 503, type: 'api_error' },
  shutting_down: { status: 503, type: 'overloaded_error' },
  overloaded: { status: 529, type: 'overloaded_error' },
} as const satisfies Record<string, { status: number; type: ErrorType }>;

export type ErrorCode = keyof typeof ERROR_CODES;
export const ErrorCodeSchema = z.enum(Object.keys(ERROR_CODES) as [ErrorCode, ...ErrorCode[]]);

export const ErrorResponseSchema = z.object({
  type: z.literal('error'),
  error: z.object({
    type: ErrorTypeSchema,
    code: ErrorCodeSchema,
    message: z.string(),
    details: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
  }),
  request_id: z.string().optional(),
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

export interface PlatoErrorOptions {
  cause?: unknown;
  details?: Array<{ path: string; message: string }>;
}

export class PlatoError extends Error {
  readonly code: ErrorCode;
  readonly type: ErrorType;
  readonly status: number;
  readonly details: Array<{ path: string; message: string }> | undefined;

  constructor(code: ErrorCode, message: string, options: PlatoErrorOptions = {}) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'PlatoError';
    this.code = code;
    this.type = ERROR_CODES[code].type;
    this.status = ERROR_CODES[code].status;
    this.details = options.details;
  }

  toResponseBody(requestId?: string): ErrorResponse {
    return {
      type: 'error',
      error: {
        type: this.type,
        code: this.code,
        message: this.message,
        ...(this.details ? { details: this.details } : {}),
      },
      ...(requestId ? { request_id: requestId } : {}),
    };
  }
}

export function isPlatoError(error: unknown): error is PlatoError {
  return error instanceof PlatoError;
}

export const REQUEST_ID_HEADER = 'request-id';
